import React, { useEffect, useState } from 'react';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { scaleLinear } from "d3-scale";
import { getCountryStats } from '../services/api'; // Ensure this is exported in api.js
import './WorldMap.css';

// URL to a valid TopoJSON world map
const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const WorldMap = () => {
    const [data, setData] = useState([]);
    const [maxValue, setMaxValue] = useState(0);
    const [tooltipContent, setTooltipContent] = useState('');

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const stats = await getCountryStats();
                // stats should be an array of { country: "Name", count: "5" }
                // Convert count to number just in case
                const formatted = stats.map(s => ({
                    country: s.country,
                    count: parseInt(s.count, 10)
                }));
                setData(formatted);
                if (formatted.length > 0) {
                    setMaxValue(Math.max(...formatted.map(d => d.count)));
                }
            } catch (err) {
                console.error("Failed to load map stats", err);
            }
        };
        fetchStats();
    }, []);

    const colorScale = scaleLinear()
        .domain([0, maxValue || 1])
        .range(["#F4F4F0", "#2D6A4F"]); // Sand to green

    return (
        <div className="world-map-container" style={{ width: '100%', height: '100%' }}>
            {/* Simple tooltip placeholder - positioned absolute */}
            <div style={{ 
                position: 'absolute', 
                top: '20px', 
                left: '50%', 
                transform: 'translateX(-50%)',
                background: 'rgba(255, 255, 255, 0.98)',
                padding: '8px 16px',
                borderRadius: '20px',
                border: '1px solid #E2E2D9',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                zIndex: 1000,
                fontSize: '0.9rem', 
                color: '#2D3748', 
                fontWeight: '500',
                pointerEvents: 'none',
                opacity: tooltipContent ? 1 : 0,
                transition: 'opacity 0.2s'
            }}>
                {tooltipContent}
            </div>

            <ComposableMap 
                projection="geoMercator"
                projectionConfig={{ 
                    scale: 140
                }} 
                style={{ width: "100%", height: "100%" }}
            >
                <ZoomableGroup center={[0, 45]} zoom={1} minZoom={0.7} maxZoom={8}>
                    <Geographies geography={GEO_URL}>
                        {({ geographies }) =>
                            geographies.map((geo) => {
                                const { name } = geo.properties;
                                const countryData = data.find((d) => d.country === name);
                                return (
                                    <Geography
                                        key={geo.rsmKey}
                                        geography={geo}
                                        onMouseEnter={() => {
                                            const count = countryData ? countryData.count : 0;
                                            setTooltipContent(`${name}: ${count} items`);
                                        }}
                                        onMouseLeave={() => {
                                            setTooltipContent("");
                                        }}
                                        style={{
                                            default: {
                                                fill: countryData ? colorScale(countryData.count) : "#F4F4F0",
                                                outline: "none",
                                                stroke: "#D1D1C7",
                                                strokeWidth: 0.5
                                            },
                                            hover: {
                                                fill: "#2D6A4F",
                                                outline: "none",
                                                cursor: "pointer"
                                            },
                                            pressed: {
                                                fill: "#1F4A36",
                                                outline: "none"
                                            }
                                        }}
                                    />
                                );
                            })
                        }
                    </Geographies>
                </ZoomableGroup>
            </ComposableMap>
        </div>
    );
};

export default WorldMap;